import React from "react";
import { Reveal, Overline } from "@/components/motion";
import { IMAGES } from "@/data/products";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { breadcrumbSchema } from "@/lib/seo";

const CHAPTERS = [
  {
    n: "01",
    title: "Our Legacy",
    body: "Rooted in a family tradition spanning 50+ years in the grain trade, SDRC Global carries forward generations of hands-on expertise in sourcing, grading and moving agricultural commodities at scale. That heritage is our foundation of trust.",
  },
  {
    n: "02",
    title: "Our Mission",
    body: "To deliver premium-quality Indian agricultural commodities while building long-term partnerships worldwide — grounded in consistency, fair pricing and complete transparency.",
  },
  {
    n: "03",
    title: "Our Vision",
    body: "To become one of India's most trusted agricultural export companies, recognised globally for quality, reliability and integrity in every consignment we ship.",
  },
];

const VALUES = ["Integrity", "Quality", "Transparency", "Customer Satisfaction", "Continuous Improvement"];

export default function About() {
  return (
    <div>
      <Seo
        title="About Us — Trusted Indian Agri Export Company"
        description="SDRC Global brings 50+ years of grain-trade heritage to the world — a trusted Indian exporter of millet, bajra, wheat and sesbania seed built on integrity, quality and transparency."
        path="/about"
        image={IMAGES.wheatField}
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }])]}
      />
      <section className="pt-36 md:pt-44 pb-14 md:pb-20 bg-brand-green-dark">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }]} className="mb-6" /></Reveal>
          <Reveal><Overline className="text-brand-gold-light mb-5">About Us</Overline></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-brand-cream leading-none max-w-4xl">
              A trusted name in the grain trade, going global.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm">
              <img src={IMAGES.wheatField} alt="Indian agricultural fields — SDRC Global sourcing regions" loading="lazy" decoding="async" sizes="(min-width: 1024px) 50vw, 100vw" className="w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="font-display text-3xl md:text-4xl text-brand-green leading-tight font-light">
              SDRC Global is the international export division created to bring high-quality Indian agricultural products to <span className="italic text-brand-gold-dark">global buyers</span>.
            </p>
            <p className="text-brand-charcoal leading-relaxed mt-6 text-lg">
              We pair decades of trade experience with modern processing standards and disciplined quality control — so buyers everywhere receive product they can rely on, shipment after shipment.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Numbered manifesto chapters */}
      <section className="bg-white border-y border-brand-line py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 py-12 md:py-16 border-b border-brand-line last:border-b-0 items-start">
                <div className="lg:col-span-1 font-display text-5xl text-brand-gold/70">{c.n}</div>
                <h2 className="lg:col-span-4 font-display text-4xl md:text-5xl font-medium text-brand-green tracking-tight">{c.title}</h2>
                <p className="lg:col-span-7 text-lg text-brand-charcoal leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["Integrity", "Quality", "Transparency", "Reliability", "Global Standards"]} />

      {/* Core values */}
      <section className="bg-brand-cream py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Overline className="text-brand-gold-dark mb-5">Core Values</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl font-medium text-brand-green tracking-tight max-w-2xl">The principles behind every partnership.</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-brand-line mt-14 border border-brand-line">
            {VALUES.map((v, i) => (
              <Reveal key={v} delay={i * 0.05}>
                <div className="bg-brand-cream p-8 h-full min-h-[180px] flex flex-col justify-between hover:bg-white transition-colors duration-500">
                  <span className="text-sm text-brand-gold-dark font-semibold">0{i + 1}</span>
                  <h3 className="font-display text-2xl font-medium text-brand-green mt-6">{v}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
